import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BriefingApiModel } from './model/briefing-api.model';
import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { BriefingListItemApiModel } from './model/briefing-list-item-api.model';
import { BriefingSendHistoryApiModel } from './model/briefing-send-history-api.model';
import { BriefingTypeEnum } from '@Monitor/Briefings/enums/briefing-type.enum';
import { NeptuneApi } from '../genericApi/neptune.api';
import { StatisticsApiInterface } from '../../Common/TargetingShared/model/statisticsReport/statistics-api.model';
import { DistributionRecipientReportApiModel } from '../../Common/TargetingShared/model/statisticsReport/distribution-report-stat-api.model';
import { EventsOverTimeQueryApiModel, EventsOverTimeApiModel } from '../../Common/TargetingShared/model/statisticsReport/events.over.time.api.model';
import { BriefingHistorySentModel } from './model/briefing-history-sent.model copy';
import { BriefingDisplayGroupPatchModel } from './model/briefing-display-group-patch.model';
import { AlertSendHistoryApiModel } from './model/alert-send-history-api.model';
import { BriefingAutoGenerateInsight } from '@Monitor/Briefings/models/briefing-auto-gen-insight.model';
import { MultiBaseAbstractApi } from '../genericApi/multi-base-abstract.api';

@Injectable()
export class BriefingsApi extends MultiBaseAbstractApi<BriefingApiModel, BriefingListItemApiModel> implements StatisticsApiInterface {

    constructor(
        private neptuneApi: NeptuneApi
    ) {
        super();
    }


    getList(type: BriefingTypeEnum = BriefingTypeEnum.BRIEFING): Observable<BriefingListItemApiModel[]> {
        return this.neptuneApi.api<BriefingListItemApiModel[]>(
            `/briefings?type=${type}`,
            null,
            HttpVerbsEnum.GET
        );
    }


    getById(id: number): Observable<BriefingApiModel> {
        return this.neptuneApi.api<BriefingApiModel>(
            `/briefings/${id}`,
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     *
     * Get briefings for a set of feed IDs
     *
     */
    getByFeedIds(ids: number[]): Observable<BriefingListItemApiModel[]> {
        let slug = '/briefings?';
        ids.forEach(id => ids.indexOf(id) === 0 ? slug += `feedId=${id}` : slug += `&feedId=${id}`);
        return this.neptuneApi.api<BriefingListItemApiModel[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }
    /**
     * Helper method: If the briefing has an ID, call update, else, create
     *
     * @param briefing
     *
     * @returns Observable<number>
     */
    save(briefing: BriefingApiModel): Observable<BriefingApiModel> {
        if (briefing.briefingId) {
            return this.updateFull(briefing);
        } else {
            return this.createFull(briefing);
        }
    }

    create(briefing: BriefingApiModel): Observable<number> {
        console.error('Method Not Implemented, Do Not Use');
        throw new Error('Method Not Implemented, Do Not Use');
    }

    update(briefing: BriefingApiModel): Observable<number> {
        console.error('Method Not Implemented, Do Not Use');
        throw new Error('Method Not Implemented, Do Not Use');
    }


    createFull(briefing: BriefingApiModel): Observable<BriefingApiModel> {
        return this.neptuneApi.api<BriefingApiModel>(
            '/briefings',
            briefing,
            HttpVerbsEnum.POST
        );
    }


    updateFull(briefing: BriefingApiModel): Observable<BriefingApiModel> {
        return this.neptuneApi.api<BriefingApiModel>(
            `/briefings/${briefing.briefingId}`,
            briefing,
            HttpVerbsEnum.PUT
        );
    }


    deleteById(id: number): Observable<number> {
        const slug = `/briefings/${id}`;

        return this.neptuneApi.api<null>(
            slug,
            null,
            HttpVerbsEnum.DELETE
        ).pipe(map(() => id));
    }

    /**
     * Directs the api to queue a briefing for sending, either as a one-off or recurring briefing
     *
     * @param id
     *
     * @returns Observable<null>
     */
    send(id: number): Observable<null> {
        return this.neptuneApi.api<null>(
            `/briefings/${id}/start`,
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * Directs the api to queue a briefing for publishing draft
     *
     * @param id
     *
     * @returns Observable<null>
     */
    publishDraft(id: number): Observable<BriefingSendHistoryApiModel> {
        return this.neptuneApi.api<null>(
            `/briefings/${id}/draft`,
            null,
            HttpVerbsEnum.POST
        );
    }

    /**
     * Sends a briefing as a preview - does not save the briefing
     */
    sendPreview(briefing: BriefingApiModel): Observable<null> {
        return this.neptuneApi.api<null>(
            '/briefings/preview/send',
            briefing,
            HttpVerbsEnum.POST
        );
    }

    /**
     * Sends a briefing as a preview - does not save the briefing
     */
    getPreviewHtml(briefing: BriefingApiModel): Observable<string> {
        return this.neptuneApi.api<string>(
            '/briefings/preview',
            briefing,
            HttpVerbsEnum.POST,
            undefined,
            'text'
        );
    }

    /**
     * Directs the api to cancel a briefing in "queued" or pause a briefing in "recurring"
     *
     * @param id
     *
     * @returns Observable<null>
     */
    cancel(id: number): Observable<null> {
        return this.neptuneApi.api<null>(
            `/briefings/${id}/cancel`,
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * Gets the history of a specific briefing
     *
     * @param id
     *
     * @returns Observable<BriefingSendHistoryApiModel[]>
     */
    getHistory(id: number): Observable<BriefingSendHistoryApiModel[]> {
        return this.neptuneApi.api<BriefingSendHistoryApiModel[]>(
            `/briefings/${id}/history`,
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * Gets the history of a specific briefing
     *
     * @param id
     *
     * @returns Observable<BriefingSendHistoryApiModel[]>
     */
    getSingleHistory(id: number): Observable<BriefingSendHistoryApiModel> {
        return this.neptuneApi.api<BriefingSendHistoryApiModel>(
            `/briefings/${id}/single-history`,
            null,
            HttpVerbsEnum.GET
        );
    }

    getEventsOverTime(id: number, query: EventsOverTimeQueryApiModel): Observable<EventsOverTimeApiModel> {
        const slug: string = `/briefings/statistics/${id}/eventsovertime`;

        return this.neptuneApi.api<EventsOverTimeApiModel>(
            slug,
            query,
            HttpVerbsEnum.POST
        );
    }

    getResourceReport(id: number, query?: any): Observable<DistributionRecipientReportApiModel> {
        const slug: string = `/briefings/statistics/${id}/recipientreport`;

        return this.neptuneApi.api<DistributionRecipientReportApiModel>(
            slug,
            query,
            HttpVerbsEnum.GET
        );
    }

    /**
     * Gets the history of a specific surge alert trigger
     * @returns Observable<AlertSendHistoryApiModel>
     */
    getAlertSendHistory(briefingSentGuid: string): Observable<AlertSendHistoryApiModel> {
        return this.neptuneApi.api<AlertSendHistoryApiModel>(
            `/briefings/statistics/${briefingSentGuid}/surgealertsentdata`,
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * Gets the history of a all surge alert trigger
     * @returns Observable<AlertSendHistoryApiModel[]>
     */
    getAllAlertSendHistory(): Observable<AlertSendHistoryApiModel[]> {
        return this.neptuneApi.api<AlertSendHistoryApiModel[]>(
            // put page size 100 because we have FE filtering to do
            '/briefings/statistics/surgealertsentdata?page=0&pageSize=100',
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * Directs the api to get sent resources list
     *
     * @param id
     *
     * @returns Observable<null>
     */
    getSentResources(guid: string | number): Observable<BriefingHistorySentModel> {
        return this.neptuneApi.api<null>(
            `/briefings/statistics/${guid}/briefingsentdates`,
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * Adds and/or removes groupID from a the provided set of briefings/alerts
     * @param addIds IDs to add the groupID to
     * @param removeIds IDs to remove the groupID from
     * @param groupId
     * @param briefingType briefings or alerts
     * @returns
     */
    updateBriefingDisplayGroup(addIds: number[], removeIds: number[], groupId: number, briefingType: BriefingTypeEnum) {
        const slug: string = `/briefings/displaygroups/${groupId}`;
        const patchModel: BriefingDisplayGroupPatchModel = {
            type: briefingType,
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    /**
     * Removes the groupID from all provided briefings/alerts
     * @param removeIds briefing/alert IDs to remove group from
     * @param briefingType briefings or alerts
     * @returns
     */
    removeBriefingsFromDisplayGroup(removeIds: number[], briefingType: BriefingTypeEnum) {
        const slug: string = '/briefings/displaygroups';
        const patchModel: BriefingDisplayGroupPatchModel = {
            type: briefingType,
            addResourceIds: [],
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    updateBriefingFavorites(addIds: number[], removeIds: number[], briefingType: BriefingTypeEnum) {
        const slug: string = '/briefings/displaygroups/favorites';
        const patchModel: BriefingDisplayGroupPatchModel = {
            type: briefingType,
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    /**
     * Sends a test email of the specified briefing to the list of recipient specified
     *
     * @param id
     * @param recipientList
     *
     * @returns Observable<null>
     */
    generateCurratedAutoGen(id: number, payload: BriefingAutoGenerateInsight): Observable<null> {
        return this.neptuneApi.api<null>(
            `/briefings/insights/${id}/auto-generate`,
            payload,
            HttpVerbsEnum.POST
        );
    }
}
